import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useCatalogo, useCliente, useClientes } from '../api/hooks';
import { Cargando, Chip, ErrorCaja } from '../componentes/ui';
import { fecha, fechaCorta } from '../lib/fechas';
import { minutos, pesos, signoPct } from '../lib/formato';
import { enlaceWhatsApp, mensajeWhatsApp } from '../lib/whatsapp';
import { totalACobrar } from '../lib/trabajo';

const sinAcentos = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function ClientesPantalla() {
  const clientes = useClientes();
  const navegar = useNavigate();
  const [q, setQ] = useState('');
  const [orden, setOrden] = useState<'ingresos' | 'recientes' | 'nombre'>('recientes');
  const lista = useMemo(() => {
    const n = sinAcentos(q.trim());
    const l = (clientes.data ?? []).filter((c) => !n || sinAcentos(`${c.nombre} ${c.correo} ${c.telefono}`).includes(n));
    return l.sort((a, b) =>
      orden === 'ingresos' ? b.ingresosCentavos - a.ingresosCentavos :
      orden === 'nombre' ? a.nombre.localeCompare(b.nombre, 'es') :
      (b.ultimoTrabajo ?? '').localeCompare(a.ultimoTrabajo ?? ''));
  }, [clientes.data, q, orden]);

  return (
    <>
      <div className="titulo"><h1>Clientes</h1><span className="sub">{clientes.data?.length ?? '–'} clientes</span></div>
      <div className="filtros">
        <input type="search" placeholder="Buscar nombre, correo o teléfono…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar cliente" />
        <div className="seg" role="group" aria-label="Ordenar">
          {(['recientes', 'ingresos', 'nombre'] as const).map((o) => (
            <button key={o} type="button" aria-pressed={orden === o} onClick={() => setOrden(o)}>{o === 'recientes' ? 'Recientes' : o === 'ingresos' ? 'Ingresos' : 'A–Z'}</button>
          ))}
        </div>
      </div>
      {clientes.isError && <ErrorCaja error={clientes.error} reintentar={() => clientes.refetch()} />}
      {clientes.isLoading ? <Cargando /> : (
        <div className="tabla-caja">
          <table>
            <thead><tr>
              <th>Cliente</th><th className="oc">Correo</th><th className="num">Trabajos</th>
              <th className="num oc">Min. estimados</th><th className="num oc">Min. reales</th><th className="num">Ingresos</th><th className="oc2">Último</th>
            </tr></thead>
            <tbody>
              {lista.length === 0 && <tr><td colSpan={7} className="vacio">Sin clientes.</td></tr>}
              {lista.map((c) => (
                <tr key={c.id} className="clic" onClick={() => navegar(`/clientes/${c.id}`)}>
                  <td className="celda-2"><Link to={`/clientes/${c.id}`} onClick={(e) => e.stopPropagation()}><b>{c.nombre}</b></Link><small>{c.telefono}</small></td>
                  <td className="oc">{c.correo}</td>
                  <td className="num">{c.trabajos}</td>
                  <td className="num oc">{minutos(c.minutosEstimados)}</td>
                  <td className="num oc">{minutos(c.minutosReales)}</td>
                  <td className="num">{pesos(c.ingresosCentavos)}</td>
                  <td className="oc2">{c.ultimoTrabajo ? fechaCorta(c.ultimoTrabajo) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function ClienteDetallePantalla() {
  const { id = '' } = useParams();
  const q = useCliente(id);
  const cat = useCatalogo();
  const navegar = useNavigate();
  if (q.isLoading) return <Cargando />;
  if (q.isError) return <ErrorCaja error={q.error} reintentar={() => q.refetch()} />;
  const { cliente, resumen, trabajos } = q.data!;
  const desviacion = resumen.minutosEstimados ? (resumen.minutosReales - resumen.minutosEstimados) / resumen.minutosEstimados : 0;

  return (
    <>
      <Link to="/clientes" className="volver">← Clientes</Link>
      <div className="cab-trabajo">
        <h1>{cliente.nombre}</h1>
        <div className="grow" />
        <a className="btn btn--wa btn--sm" target="_blank" rel="noopener noreferrer"
          href={enlaceWhatsApp(cliente.telefono, `Hola ${cliente.nombre.split(' ')[0]}, te escribimos de Emisha Láser.`)}>WhatsApp</a>
        <a className="btn btn--sm" href={`mailto:${cliente.correo}`}>Correo</a>
      </div>

      <div className="tiles">
        <div className="tile"><b>{resumen.trabajos}</b><span>trabajos</span></div>
        <div className="tile"><b>{pesos(resumen.ingresosCentavos)}</b><span>ingresos cobrados</span></div>
        <div className="tile"><b>{minutos(resumen.minutosEstimados)}</b><span>láser estimado</span></div>
        <div className="tile"><b>{minutos(resumen.minutosReales)}</b><span>láser real{resumen.minutosReales ? ` (${signoPct(desviacion)})` : ''}</span></div>
      </div>

      <div className="rejilla-detalle">
        <div className="tabla-caja">
          <table>
            <thead><tr><th>Folio</th><th className="oc2">Recibido</th><th>Estado</th><th className="oc">Material</th><th className="num oc">Min. est. / real</th><th className="num">Total</th></tr></thead>
            <tbody>
              {trabajos.length === 0 && <tr><td colSpan={6} className="vacio">Sin trabajos.</td></tr>}
              {trabajos.map((t) => {
                const mat = cat.material.get(t.solicitud.materialId);
                return (
                  <tr key={t.id} className="clic" onClick={() => navegar(`/trabajos/${t.id}`)}>
                    <td><Link className="folio" to={`/trabajos/${t.id}`} onClick={(e) => e.stopPropagation()}>{t.folio}</Link></td>
                    <td className="oc2">{fechaCorta(t.creadoEn)}</td>
                    <td><Chip estado={t.estado} /></td>
                    <td className="oc">{mat ? `${mat.nombre} ${mat.espesorMm} mm` : '—'}</td>
                    <td className="num oc">{minutos(t.minutosEstimados)} / {t.minutosReales ? minutos(t.minutosReales) : '—'}</td>
                    <td className="num">{pesos(totalACobrar(t))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <aside className="tarjeta">
          <div className="h3">Contacto</div>
          <div className="linea"><span className="muted">Teléfono</span><span>{cliente.telefono}</span></div>
          <div className="linea"><span className="muted">Correo</span><a href={`mailto:${cliente.correo}`}>{cliente.correo}</a></div>
          <div className="linea"><span className="muted">Último trabajo</span><span>{resumen.ultimoTrabajo ? fecha(resumen.ultimoTrabajo) : '—'}</span></div>
          {cliente.notas && <><div className="h3" style={{ marginTop: 12 }}>Notas</div><p style={{ fontSize: '.9rem' }}>{cliente.notas}</p></>}
          {trabajos[0] && (
            <p style={{ marginTop: 12 }}>
              <a className="enlace-btn" target="_blank" rel="noopener noreferrer"
                href={enlaceWhatsApp(cliente.telefono, mensajeWhatsApp('general', { nombre: cliente.nombre, folio: trabajos[0].folio, totalCentavos: null }))}>
                Escribir sobre {trabajos[0].folio}
              </a>
            </p>
          )}
        </aside>
      </div>
    </>
  );
}
